import { useMemo, useState } from 'react'
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import {
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_VIEW,
  dayCountFor,
  readStoredCalendarView,
  writeStoredCalendarView,
  type CalendarView,
} from './calendarView'

export function toDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Saturday or Sunday. Neither is ever drawn — the window steps straight over both. */
function isWeekend(date: Date): boolean {
  const dow = date.getDay()
  return dow === 0 || dow === 6 // 0 = Sunday, 6 = Saturday
}

/**
 * Move `n` **working** days, in either direction.
 *
 * Counts only days that will actually be drawn, so `next()` from a Friday lands on Monday and
 * `prev()` from a Monday lands on Friday — the weekend costs no steps because it occupies no
 * column.
 */
function addWorkingDays(date: Date, n: number): Date {
  if (n === 0) return date
  const step = n > 0 ? 1 : -1
  let remaining = Math.abs(n)
  let cur = date
  while (remaining > 0) {
    cur = addDays(cur, step)
    if (!isWeekend(cur)) remaining -= 1
  }
  return cur
}

/** Forward to the next Monday. Saturday costs two days, Sunday one, a weekday nothing. */
function snapOffWeekend(date: Date): Date {
  let cur = date
  while (isWeekend(cur)) cur = addDays(cur, 1)
  return cur
}

/**
 * Returns today as the anchor. If today falls on a weekend, snaps to the following Monday.
 */
export function getInitialAnchor(today: Date): Date {
  return snapOffWeekend(toDateOnly(today))
}

/**
 * The visible working-day window as one value — what `WorkerCalendarWidget` takes as its only prop.
 * One day or three, depending on `view`. The page owns the hook (it is URL state, a routing
 * concern); the widget owns everything else.
 */
export interface WorkerCalendarDays {
  /** `day` = one column at full width, `three` = the three-working-day window. */
  view: CalendarView
  setView: (view: CalendarView) => void
  days: Date[]
  dayIsos: string[]
  from: string
  to: string
  todayIso: string
  prev: () => void
  next: () => void
  goToToday: () => void
}

export function useWorkerCalendarDays(): WorkerCalendarDays {
  const today = toDateOnly(new Date())

  /*
   * The URL wins, so a shared or reloaded link always opens the view it names. Without a param the
   * browser's last choice applies. Read once: the default must not change under nuqs mid-session
   * just because `setView` has since written a new value to storage.
   */
  const [storedView] = useState(readStoredCalendarView)
  const [view, setViewParam] = useQueryState(
    'calView',
    parseAsStringLiteral(CALENDAR_VIEWS).withDefault(storedView ?? DEFAULT_CALENDAR_VIEW),
  )

  const [anchorIso, setAnchorIso] = useQueryState(
    'calAnchor',
    parseAsString.withDefault(toIsoDate(getInitialAnchor(today))),
  )

  const anchor = useMemo(() => {
    const [y, m, d] = anchorIso.split('-').map(Number)
    const parsed = new Date(y, m - 1, d)
    const valid = Number.isNaN(parsed.getTime()) ? getInitialAnchor(today) : parsed
    return snapOffWeekend(valid)
  }, [anchorIso, today])

  const dayCount = dayCountFor(view)
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => (i === 0 ? anchor : addWorkingDays(anchor, i))),
    [anchor, dayCount],
  )

  const from = useMemo(
    () => new Date(days[0].getFullYear(), days[0].getMonth(), days[0].getDate(), 0, 0, 0, 0).toISOString(),
    [days],
  )
  const to = useMemo(() => {
    const last = days[days.length - 1]
    return new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999).toISOString()
  }, [days])

  const todayIso = toIsoDate(today)
  const dayIsos = days.map(toIsoDate)

  function next() {
    setAnchorIso(toIsoDate(addWorkingDays(anchor, 1)))
  }

  function prev() {
    setAnchorIso(toIsoDate(addWorkingDays(anchor, -1)))
  }

  function goToToday() {
    setAnchorIso(toIsoDate(getInitialAnchor(today)))
  }

  /** The anchor stays put: 3 → 1 keeps the first column's day, 1 → 3 opens a window starting on it. */
  function setView(next: CalendarView) {
    writeStoredCalendarView(next)
    setViewParam(next)
  }

  return { view, setView, days, dayIsos, from, to, todayIso, prev, next, goToToday }
}
